import React, { useMemo, memo, useState, useEffect } from "react";
import SimpleQuestion from "./SimpleQuestion";
import * as theme from "../theme";
import { NumericFormat } from "react-number-format";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import CancelIcon from "@material-ui/icons/Cancel";
import { Button } from "react-bootstrap";
import InfoIcon from "@material-ui/icons/Info";
import ReactTooltip from "react-tooltip";
import _ from "lodash";
import AutoFitTextDiv from "../general/AutoFitTextDiv";
import * as XLSX from "xlsx/xlsx.mjs";
import { toast } from "react-toastify";
import {  getLanguageCode, getQFuserMode } from '../store/components'
import { getBrokerInformation } from "../store/broker";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import frLocale from "date-fns/locale/fr";
import enLocale from "date-fns/locale/en-US";
import { format } from 'date-fns';
import {
  changeNodeOfScheduleQuestion,
  updateProspectForms,
  AddtoClientLog,
  getProspectDoc,
  getFormState,
  getExcelScheduleFile,
  getScheduleFile,
  scheduleFileStart,
  uploadDocument,
  getIsProspectDocument
} from "../store/qf";
import { INSURER, PossibleScheduleInputType, CLIENT } from "../store/businesslogic/hardwiredlogic";
import translations from "../utils/translations";
import { List, AutoSizer } from "react-virtualized";
import { Switch, FormControlLabel} from "@material-ui/core";

class LocalUTCUtils extends DateFnsUtils {
  date = (value) => {
    if (value === null) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return super.date(value);
  };

  format = (date, formatString) => {
    if (!date) return '';
    return format(date, formatString, { locale: this.locale });
  };

  parse = (value, formatString) => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return super.parse(value, formatString);
  };
}

const Schedule = ({ text, node, handleAnswerChange, qfusermode }) => {
  const brokerInfo = useSelector(getBrokerInformation);
  const languagepreference = brokerInfo.information.languagepreference;
  const { logic } = node;

  const strings = useMemo(() => ({
    scheduleButtonString: logic?.buttonString?.[languagepreference] || "Schedule",
    fileUploadButtonString: logic?.fileUploadButtonString?.[languagepreference] || "",
    popupHeading: logic?.popupHeading?.[languagepreference] || "",
    popupDescription: logic?.popupDescription?.[languagepreference] || "",
    popupAddItemButtonString: logic?.popupAddItemButtonText?.[languagepreference] || "",
  }), [logic, languagepreference]);

  return (
    <SimpleQuestion
      leftString={text}
      node={node}
      rightComponent={
        <ScheduleInput
          node={node}
          handleAnswerChange={handleAnswerChange}
          {...strings}
          qfusermode={qfusermode}
        />
      }
    />
  );
};

const ScheduleInput = memo(({ node, handleAnswerChange, scheduleButtonString, fileUploadButtonString, popupHeading, popupDescription, popupAddItemButtonString, qfusermode }) => {
  const dispatch = useDispatch();
  const [renderValue, setRenderValue] = useState(node.answer?.mainanswer || "");
  const inputType = node?.logic?.inputType || "";
  const brokerInfo = useSelector(getBrokerInformation);
  const handleMainAnswer = (value) => handleAnswerChange({ ...node.answer, mainanswer: value });

  useEffect(() => {
    if (renderValue !== node.answer?.mainanswer) {
      handleMainAnswer(renderValue)
    }
  }, [node.answer?.mainanswer, renderValue]);

  const inputComponent = useMemo(() => {
    const props = {
      value: renderValue,
      readOnly: qfusermode === INSURER,
      style: { ...theme.inputStyle, width: inputType === PossibleScheduleInputType.simplestring ? "10vw" : "8vw" },
      id: `numberInput_${node.id}`,
      onChange: (e) => setRenderValue(e.target.value),
      onBlur: (e) => handleMainAnswer(e.target.value),
    };
    return inputType === PossibleScheduleInputType.simplestring ? (
      <input {...props} type="text" />
    ) : inputType === PossibleScheduleInputType.number ? (
      <input {...props} type="number" placeholder={translations[brokerInfo.information.languagepreference].enterNumber} />
    ) : inputType === PossibleScheduleInputType.currency ? (
      <NumericFormat {...props} prefix="$" thousandSeparator inputMode="numeric" onValueChange={(v) => setRenderValue(v.value)} />
    ) : null;
  }, [inputType, node.id, renderValue, qfusermode]);

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
      {inputComponent}
      <ScheduleButton
        onClick={() => {
          dispatch(changeNodeOfScheduleQuestion({ node, changedValue: true }));
          if (_.isEmpty(node?.answer?.tablearray)) handleAnswerChange(addOneRow({ node }).answer);
        }}
        text={scheduleButtonString}
      />
      <ScheduleModal
        node={node}
        handleAnswerChange={handleAnswerChange}
        fileUploadButtonString={fileUploadButtonString}
        popupHeading={popupHeading}
        popupDescription={popupDescription}
        popupAddItemButtonString={popupAddItemButtonString}
        qfusermode={qfusermode}
        setRenderValue={setRenderValue}
        renderValue={renderValue}
      />
    </div>
  );
});

const ScheduleButton = memo(({ onClick, text }) => (
  <Button
    style={{
      minWidth: "6vw",
      minHeight: "4vmin",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.COLORS.secondary_orange,
      borderRadius: 15,
      color: "white",
      marginLeft: "1vw",
      fontSize: text.length > 10 ? "0.6rem" : "1rem",
      border: 0,
    }}
    onClick={onClick}
  >
    {text}
  </Button>
));

const ScheduleModal = memo(({ setRenderValue, renderValue, node, handleAnswerChange, popupHeading, fileUploadButtonString, popupDescription, popupAddItemButtonString, qfusermode }) => {
  const popupWidth = Math.min(Math.max(node?.logic?.popupWidth || 70, 10), 90);
  return (
    <Modal
      isOpen={node.modalstate}
      ariaHideApp={false}
      shouldCloseOnEsc={false}
      style={{
        overlay: { backgroundColor: theme.COLORS.rgba_primary_navyblue },
        content: {
          height: "87vh",
          width: `${popupWidth}vw`,
          top: `${Math.floor((100 - 87) / 2)}vh`,
          left: `${Math.floor((100 - popupWidth) / 2)}vw`,
          background: theme.COLORS.primary_navyblue,
          border: "8px solid white",
          borderRadius: "20px",
          padding: 0,
          overflow: "hidden",
        },
      }}
    >
      <ScheduleModalContent
        node={node}
        handleAnswerChange={handleAnswerChange}
        fileUploadButtonString={fileUploadButtonString}
        popupHeading={popupHeading}
        popupDescription={popupDescription}
        popupAddItemButtonString={popupAddItemButtonString}
        qfusermode={qfusermode}
        setRenderValue={setRenderValue}
        renderValue={renderValue}
      />
    </Modal>
  );
});

const ScheduleModalContent = memo(({setRenderValue, renderValue, node, handleAnswerChange, popupHeading, fileUploadButtonString, popupDescription, popupAddItemButtonString, qfusermode }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [showArchived, setShowArchived] = useState(false)
  const brokerInfo = useSelector(getBrokerInformation);
  const scheduleFile = useSelector(getScheduleFile)
  const languagepreference = brokerInfo.information.languagepreference;
  const qfUserMode = useSelector(getQFuserMode);
  const columnDetails = useMemo(() => node?.logic?.columnDetails || [], [node?.logic?.columnDetails]);
  const [hover, setHover] = useState(false);
  const gotProspectDetails = useSelector(getProspectDoc);
  const gotFormState = useSelector(getFormState);
  const [mapHeader, setMapHeader] = useState({})
  const handleOnCSVFileChange = (e) => setFile(e.target.files[0]);

  const handleOnCSVFileDownload = (e) => {
    e.preventDefault();
    const arr = Object.values(node.answer.tableanswer || {});
    const headers = arr.length ? [...new Set(arr.flatMap((row) => Object.keys(row || {})))] : columnDetails.map((x) => x.columnHeading[languagepreference]);
    const csvData = [headers, ...arr.map((row) => headers.map((key) => row?.[key] ?? ""))];
    const csvText = csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");
    const encodedUri = `data:attachment/csv;charset=utf-8,${encodeURI(csvText).replace(/#/g, "%23")}`;
    const link = document.createElement("a");
    link.href = encodedUri;
    link.download = `schedules_${node.id}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOnXLSXFileDownload2 = (e) => {
    e.preventDefault();
    // Get the data and determine headers
    const arr = Object.values(node.answer.tableanswer || {});
    const headers = arr.length 
      ? [...new Set(arr.flatMap((row) => Object.keys(row || {})))] 
      : columnDetails.map((x) => x.columnHeading[languagepreference]);
    // Prepare data for worksheet (without the offset yet)
    const worksheetData = [
      headers, // Headers row
      ...arr.map((row) => headers.map((key) => row?.[key] ?? ""))
    ];
    // Create worksheet starting at B2 (A1 is B1 in 0-based indexing)
    const ws = XLSX.utils.aoa_to_sheet(worksheetData, { origin: "B2" });
    // Set column widths for wrapping
    const colWidths = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;
    // Define range of data (including headers)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const dataStartRow = 1; // Row 2 (0-based, since B2)
    const dataEndRow = range.e.r;
    const dataStartCol = 1; // Column B (0-based)
    const dataEndCol = range.e.c;
    // Apply styles
    for (let row = dataStartRow; row <= dataEndRow; row++) {
      for (let col = dataStartCol; col <= dataEndCol; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellRef]) continue; // Skip empty cells
        // Header row styling (row 1 in our offset, which is dataStartRow)
        if (row === dataStartRow) {
          ws[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "E0E0E0" } }, // Light gray background
            alignment: { wrapText: true, vertical: "center", horizontal: "center" },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" }
            }
          };
        } else {
          // Data cells styling
          ws[cellRef].s = {
            alignment: { wrapText: true },
            border: {
              top: { style: "thin" },
              bottom: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" }
            }
          };
        }
      }
    }
    // Update worksheet range to include the offset
    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 }, // A1
      e: { r: dataEndRow, c: dataEndCol } // End of data
    });
    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Schedule");
    // Generate and download XLSX file
    XLSX.writeFile(wb, `schedules_${node.id}.xlsx`, {
      bookType: "xlsx",
      type: "binary",
      compression: true
    });
  };

  const handleOnXLSXFileDownload = (e) => {
    e.preventDefault();
    // Get the data and determine headers
    //console.log(gotProspectDetails)
    // console.log(gotProspectDetails.formstate.a.nodestate[10501])
    // return;
    const arr = Object.values(node.answer.tableanswer || {});
    const headers = arr.length 
      ? [...new Set(arr.flatMap((row) => Object.keys(row || {})))] 
      : columnDetails.map((x) => x.columnHeading[languagepreference]);
    let titleText = node.text[languagepreference]
    let descriptionText = node.hint[languagepreference]?.replace(/<[^>]*>/g, ""); 
    let fullNameText = gotFormState?.a?.nodestate?.[10501]?.answer;
    // Prepare data for worksheet (without the offset yet)
    dispatch(getExcelScheduleFile({tableanswer: node.answer.tableanswer, headers: columnDetails.map((x) => x.columnHeading[languagepreference]), titleText, descriptionText, fullNameText  }))
  };

  const handleOnCSVFileSubmit = (e) => {
    e.preventDefault();
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target.result;
      if (file.type === "text/csv") updateRowsAndSetStore(data);
      else if (file.name.includes("xlsx")) {
        const workbook = XLSX.read(data);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        updateRowsAndSetStore(XLSX.utils.sheet_to_csv(sheet), XLSX.utils.sheet_to_json(sheet, { skipHeader: true, header: 1, defval:"" }));
      } else toast.success("File is not CSV or XLSX type");
    };
    file.type === "text/csv" ? reader.readAsText(file) : reader.readAsArrayBuffer(file);
    //lets push file to backend to upload to s3
    const formData = new FormData();
    formData.append("file", file);
    let file_data = {
      insurerCode: "test",
      file,
      fileName: file.name,
      fileType: 'schedule',
      size: file.size,
      addtransaction:true,
      userid: gotProspectDetails.userid
    };
    dispatch(uploadDocument(formData, file_data));
  };

  const updateRowsAndSetStore = (csvData, jsonData = null) => {
    const newNodeTableAnswer = {};
    let counter = 1;
    const headers = columnDetails.map((x) => x.code);
    headers.push('archived') // add archived
    if (jsonData) {      
      let headers_csv = jsonData[0];
      let startDataIndex = 1;
      if(headers_csv.filter(x => !['', null].includes(x)).length < 3 ){
        for(let [index , rowx] of jsonData.entries()){
          if(rowx.filter(x => !['', null].includes(x)).length > 3){
            headers_csv = rowx;
            startDataIndex = index + 1;
            break
          };
        }
      }
      for (let i = startDataIndex; i < jsonData.length; i++) {
        const row = jsonData[i];
        //console.log(row)
        let tObj = {}
        if(Object.keys(mapHeader).length){
          for(let key in mapHeader){
            const index = headers_csv.indexOf(mapHeader[key]);
            tObj[key] = row[index]
          }
        }
        else {
          tObj = Object.fromEntries(headers.map((h, idx) => [h, row[idx] || ""]));
          if(!["", "true", "false", true, false].includes(tObj.archived)){
            tObj.archived = ""
          }
          else if ([true, false].includes(tObj.archived)){
            tObj.archived = tObj.archived.toString()
          }
        }
        if (Object.values(tObj).some(Boolean)) newNodeTableAnswer[counter++] = tObj;
      }
    } else {
      const lines = csvData.split(/\r\n|\n|\r/).slice(1).filter(line => line.trim() !== "")
      const headers_csv = csvData.split(/\r\n|\n|\r/)[0].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => v.replace(/^"|"$/g, "").trim());
      for (const line of lines) {
        const values = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map((v) => v.replace(/^"|"$/g, ""));
        // console.log(values);
        let tObj = {};
        if(Object.keys(mapHeader).length){
          for(let key in mapHeader){
            const index = headers_csv.indexOf(mapHeader[key]);
            tObj[key] = values[index]
          }
        }else{
          // mapper wasnt used, we go by order instead
          tObj = Object.fromEntries(headers.map((h, idx) => [h, values[idx] || ""]));
        }
        if (Object.values(tObj).some((v) => v && v !== ",")) newNodeTableAnswer[counter++] = tObj;
      }
    }
    const newNodeAnswers = { tableanswer: newNodeTableAnswer, tablearray: Object.keys(newNodeTableAnswer).filter(key => newNodeTableAnswer[key]?.archived !== "true").map(Number), archivedarray: Object.keys(newNodeTableAnswer).filter(key => newNodeTableAnswer[key]?.archived === "true").map(Number), mainanswer:renderValue  };
    //console.log(newNodeAnswers)
    handleAnswerChange(newNodeAnswers);
    dispatch(updateProspectForms({ updateFormsOrder: true }));
    if (qfUserMode === CLIENT) dispatch(AddtoClientLog({ newentry: { question: "Schedule Question", old_answer: "", new_answer: "New File has been imported" } }));
    setFile(null);
  };

  useEffect(() => {
    const onKeyPressed = (e) => { if (e.keyCode === 27) dispatch(changeNodeOfScheduleQuestion({ node, changedValue: false })); };
    document.addEventListener("keydown", onKeyPressed);
    return () => document.removeEventListener("keydown", onKeyPressed);
  }, [dispatch, node]);

  useEffect( () => {
    if(scheduleFile){
      // Convert Base64 to Blob
      const byteCharacters = atob(scheduleFile);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "schedule.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      //make schedule file null again so useEffect doesnt re-trigger
      dispatch(scheduleFileStart());
    }
  }, [scheduleFile])

  useEffect(() => {
    let newTableArray = (node.answer?.tablearray || []).filter(id => !(node.answer?.archivedarray || []).includes(id));
    const newNodeAnswers = { ...node.answer, tablearray: newTableArray };
    handleAnswerChange(newNodeAnswers);
  }, [])

  const valueKey = columnDetails.find((x) => x.valueColumn)?.code || "value";
  const showRunningTotal = !columnDetails.some((x) => x.hidecalculateValue);
  const tempSum = useMemo(() => {
    return Object.values(node.answer.tableanswer || {}).reduce((sum, row) => {
      const val = row?.[valueKey];
      const isArchived = row?.archived === "true";
      return sum + (val && (!isArchived) && node?.logic?.inputType === "currency" ? parseFloat(val) || 0 : node?.logic?.inputType === "number" ? 1 : 0);
    }, 0).toFixed(2);
  }, [node.answer.tableanswer, valueKey, node?.logic?.inputType]);

  const mainAnswer = typeof node.answer.mainanswer === "string" ? node.answer.mainanswer.replace(/^\D+/g, "") : node.answer.mainanswer;
  const dollarDifference = (mainAnswer - tempSum).toFixed(2);
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      <div style={{ position: "absolute", top: 10, right: 10 }}>
        <CancelIcon
          fontSize="large"
          style={{ cursor: "pointer", color: "white" }}
          onClick={() => {
            if (tempSum && (!node.answer.mainanswer || node.answer.mainanswer === "") ) {
              handleAnswerChange({ ...node.answer, mainanswer: tempSum });
              setRenderValue(tempSum)
              dispatch(updateProspectForms({ updateFormsOrder: true }));
            }
            dispatch(changeNodeOfScheduleQuestion({ node, changedValue: false }));
          }}
        />
      </div>
      <div style={{ width: "100%", minHeight: "15%", margin: "1.5% 2vw 0.5%", fontSize: "1.9vh" }}>
        <div style={{ color: "white", fontFamily: "GibsonSemiBd" }}>{popupHeading}</div>
        <div style={{ color: "white", fontSize: "1.5vh" }}>{popupDescription}</div>
        {!gotProspectDetails?.isbinded && (
          <div style={{ color: "white", fontSize: "1.5vh", marginTop: "2%" }}>
            <form>
              <InfoIcon style={{ color: hover ? theme.COLORS.secondary_skyblue : null, margin: "0 1%" }} onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)} data-tip data-for="registerTip2" />
              <ReactTooltip id="registerTip2" place="right" effect="solid">{translations[languagepreference].tooltipImportExcel}</ReactTooltip>
              <input type="file" id="csvFileInput" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" style={{ width: "24rem" }} onChange={handleOnCSVFileChange} />
              {file && (
                <button
                  style={{ color: "white", height: "3.5vh", fontFamily: "GibsonSemiBd", fontSize: 12, backgroundColor: theme.COLORS.secondary_skyblue, border: 0, width: "9vw" }}
                  onClick={handleOnCSVFileSubmit}
                >
                  {translations[languagepreference].importCSV}
                </button>
              )}
              <button
                style={{ color: "white", height: "3.5vh", fontFamily: "GibsonSemiBd", fontSize: 12, backgroundColor: theme.COLORS.secondary_skyblue, border: 0, marginLeft: "2%", width: "9vw" }}
                onClick={handleOnXLSXFileDownload}
              >
                {node.answer?.tablearray?.length > 1 ? translations[languagepreference].exportCSV : translations[languagepreference].exportTemplate}
              </button>
              <FormControlLabel
                style={{ marginLeft: "2vw" }}
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e)=> {
                      const isChecked = e.target.checked;
                      setShowArchived(isChecked);
                      // console.log(node.answer)
                      let newState = !showArchived;
                      let newTableArray = []
                      if (isChecked) {
                        // Combine tablearray and archivedarray, removing duplicates
                        newTableArray = [...new Set([...(node.answer?.tablearray || []), ...(node.answer?.archivedarray || [])])];
                      } else {
                        // Filter out archived items
                        newTableArray = (node.answer?.tablearray || []).filter(
                          id => !(node.answer?.archivedarray || []).includes(id)
                        );
                      }
                      const newNodeAnswers = { ...node.answer, tablearray: newTableArray };
                      handleAnswerChange(newNodeAnswers);
                    }}
                  />
                }
                label={translations[languagepreference].showArchrive}
              />
            </form>
          </div>
        )}
        {gotProspectDetails?.isbinded && (
          <button
            style={{ color: "white", height: "3.5vh", fontFamily: "GibsonSemiBd", fontSize: 12, backgroundColor: theme.COLORS.secondary_skyblue, border: 0, marginLeft: "2%", width: "9vw" }}
            onClick={handleOnCSVFileDownload}
          >
            {node.answer?.tablearray?.length ? translations[languagepreference].exportCSV : translations[languagepreference].exportTemplate}
          </button>
        )}
      </div>
      <ImportFileMapper
        columnDetails={columnDetails}
        languagepreference={languagepreference}
        file={file}
        mapHeader={mapHeader}
        setMapHeader={setMapHeader}
      />
      <div style={{ width: "100%", height: "calc(100% - 16%)" }}>
        <div style={{ width: "100%", height: "73%", padding: "0 2vw" }}>
          <TableSchedule node={node} handleAnswerChange={handleAnswerChange} qfusermode={qfusermode} columnDetails={columnDetails} showArchived={showArchived} />
        </div>
        <div style={{ display: "flex", flexDirection: "row", position: "relative", width: "100%" }}>
          <div style={{ width: "100%", height: "20%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Button
              style={{ width: "8vw", height: "4vh", borderRadius: 15, color: "white", backgroundColor: theme.COLORS.secondary_orange, border: 0 }}
              onClick={() => qfusermode !== INSURER && handleAnswerChange(addOneRow({ node }).answer)}
            >
              <AutoFitTextDiv text={popupAddItemButtonString} textsize={20} />
            </Button>
          </div>
          {showRunningTotal && (
            <div style={{ position: "absolute", right: "2vw", top: "50%", transform: "translateY(-50%)", height: "5em", color: "white", fontSize: "medium" }}>
              {translations[languagepreference].runningTotal} {Number(tempSum).toLocaleString(navigator.language, { minimumFractionDigits: 2 })}
              <br />
              {node.answer?.mainanswer && (
                <span>
                  {translations[languagepreference].amountToBeAdded} {Number(dollarDifference).toLocaleString(navigator.language, { minimumFractionDigits: 2 })}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const addOneRow = ({ node }) => {
  const currentAnswer = node.answer || { tablearray: [], tableanswer: {}, mainanswer:null, archivedarray: [] };
  const tablearray = currentAnswer.tablearray || [];
  const newestIdx = tablearray.length ? Number(tablearray[tablearray.length - 1]) + 1 : 0;
  return {
    ...node,
    answer: {
      ...currentAnswer,
      tablearray: [ ...tablearray, newestIdx ],
      tableanswer: { ...currentAnswer.tableanswer, [newestIdx]: {} },
      archivedarray: currentAnswer.archivedarray || []
    }
  };
};

const TableSchedule = memo(({ node, handleAnswerChange, qfusermode, columnDetails, showArchived }) => {
  const widthDistribution = useMemo(() => ({
    itemnumber: "10%",
    desc: "25%",
    serial: "20%",
    coverage: "10%",
    purchase: "15%",
    limit: "calc(100% - 10% - 25% - 20% - 10% - 15%)",
  }), []);

  const rowRenderer = ({ index, key, style }) => {
    const rowid = node.answer.tablearray[index];
    let isarchived = node.answer.tableanswer[rowid]?.archived == "true";
    return (
      <div key={key} style={style}>
        <EachScheduleRow
          rowid={rowid}
          node={node}
          handleAnswerChange={handleAnswerChange}
          widthDistribution={widthDistribution}
          columnDetails={columnDetails}
          qfusermode={qfusermode}
          showArchived={showArchived}
        />
      </div>
    );
  };

  return (
    <div style={{ width: "100%", height: "90%" }}>
      <div style={{ minHeight: "15%" }}>
        <TableHeaderSchedule widthDistribution={widthDistribution} columnDetails={columnDetails} nodeAnswers={node.answer} node={node} showArchived={showArchived} />
      </div>
      <div style={{ width: "100%", height: "85%" }}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              height={height}
              width={width}
              rowCount={node.answer?.tablearray?.length || 0}
              rowHeight={40} // Adjust based on your row height
              rowRenderer={rowRenderer}
              overscanRowCount={10}
            />
          )}
        </AutoSizer>
      </div>
    </div>
  );
});

const EachScheduleRow = memo(({ rowid, node, handleAnswerChange, widthDistribution, columnDetails, qfusermode, showArchived }) => {
  const dispatch = useDispatch();

  const handleAnswer = (value, key) => {
    const newAnswer = {
      ...node.answer,
      tableanswer: { ...node.answer.tableanswer, [rowid]: { ...node.answer.tableanswer[rowid], [key]: value } },
    };
    if(key == 'archived'){
      if(value == 'false'){
        newAnswer.archivedarray = newAnswer.archivedarray.filter(x => x !== rowid )
        newAnswer.tableanswer[rowid].archived = 'false';
      }
      else if (value == 'true'){
        newAnswer.archivedarray = [...newAnswer.archivedarray, rowid];
        newAnswer.tableanswer[rowid].archived = 'true';
        newAnswer.tablearray.filter(x => x != key )
      }
    }
    //console.log(newAnswer)
    handleAnswerChange(newAnswer);
    if (qfusermode === CLIENT) {
      dispatch(AddtoClientLog({
        newentry: { question: `Schedule Answer Change at row ${rowid} for ${key}`, old_answer: node.answer.tableanswer[rowid][key], new_answer: value },
      }));
    }
  };

  const deleteRow = () => {
    console.log(node.answer.tableanswer);
    const newAnswers = {
      ...node.answer,
      tableanswer: {
        ...node.answer.tableanswer,
        [rowid]: {
          ...node.answer.tableanswer[rowid],
          archived: "true"
        }
      },
      // tablearray remains unchanged since we're not removing the entry
      tablearray: node.answer.tablearray.filter(id => id !== rowid),
      archivedarray: [...(node.answer.archivedarray || []), rowid]
    };
    handleAnswerChange(newAnswers);
  };

  return (
    <div style={{ width: "100%", backgroundColor: "white", display: "flex", height: "4vh" }}>
      <CancelIcon fontSize="small" style={{ cursor: "pointer", color: "black" }} onClick={deleteRow} />
      {columnDetails.map((columnDetail, index) => (
        <EachCellOfScheduleTable
          key={`index${index}rowid${rowid}`}
          columnDetail={columnDetail}
          handleAnswer={handleAnswer}
          value={node.answer?.tableanswer?.[rowid]?.[columnDetail.code] || ""}
          qfusermode={qfusermode}
        />
      ))}
      {
        showArchived && <EachCellOfScheduleTable
            key={`archive_rowid${rowid}`}
            columnDetail={{'code':'archived' , selectoptions: ['true', 'false'], type:'select' }}
            handleAnswer={handleAnswer}
            value={node.answer?.tableanswer?.[rowid]?.['archived'] == "true" ? "true": "false" }
            qfusermode={qfusermode}
          />
      }
    </div>
  );
});

const EachCellOfScheduleTable = memo(({ columnDetail, handleAnswer, value, qfusermode }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRight: `1px solid ${theme.COLORS.tertiary_ligthindigo}`,
      padding: "0 1vmin",
      width: columnDetail?.columnWidth ? `${columnDetail.columnWidth}%` : "10%",
    }}
  >
    <CommonInputValue value={value} handleAnswer={handleAnswer} columnDetail={columnDetail} qfusermode={qfusermode} />
  </div>
));

const CommonInputValue = memo(({ value, handleAnswer, columnDetail, qfusermode }) => {
  const [inputValue, setInputValue] = useState(value);
  const languagepreference = useSelector(getBrokerInformation).information.languagepreference;

  useEffect(() => setInputValue(value), [value]);

  if (columnDetail.type === "select") {
    return (
      <select
        value={inputValue}
        onChange={(e) => { setInputValue(e.target.value); handleAnswer(e.target.value, columnDetail.code); }}
        disabled={qfusermode === INSURER}
        style={{ width: "100%", height: "3vh", fontSize: "11px" }}
      >
        {columnDetail.selectoptions.map((opt, idx) => {
          return (
          <option key={idx} value={opt[languagepreference] || opt}>{opt[languagepreference] || opt}</option>
          )
        }
        )}
      </select>
    );
  } else if (columnDetail.type === "date") {
    const locale = languagepreference === "fr" ? frLocale : enLocale;
    return (
      <MuiPickersUtilsProvider utils={LocalUTCUtils} locale={locale}>
        <KeyboardDatePicker
          autoOk={true}
          placeholder={languagepreference === "fr" ? "aaaa-mm-jj" : "yyyy-mm-dd"}
          cancelLabel={translations[languagepreference].cancel}
          todayLabel={translations[languagepreference].today}
          showTodayButton={true}
          value={inputValue || null}
          format="yyyy-MM-dd"
          inputFormat="yyyy-MM-dd"
          onChange={(date) => {
            const formatted = date
              ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
              : '';
            setInputValue(formatted);
            handleAnswer(formatted, columnDetail.code);
          }}
          disabled={qfusermode === INSURER}
          style={{ width: "100%", height: "3vh", fontSize: "11px" }}
          InputProps={{
            style: {
              width: "100%",
              height: "3vh",
              fontSize: "11px",
              backgroundColor: theme.COLORS.tertiary_ligthindigo,
              borderRadius: 5,
              border: 0
            }
          }}
        />
      </MuiPickersUtilsProvider>
    );
  } else {
    return (
      <input
        type={columnDetail.type === "number" ? "number" : "text"}
        value={inputValue || ""}
        onChange={(e) => {
          const val = e.target.value;
          //if (columnDetail.type === "number" && val && !/^\d{1,}(\.\d{0,4})?$/.test(val)) return;
          setInputValue(val);
        }}
        onBlur={(e) => handleAnswer(e.target.value, columnDetail.code)}
        readOnly={qfusermode === INSURER}
        style={{ border: 0, width: "100%", backgroundColor: theme.COLORS.tertiary_ligthindigo, fontFamily: "GibsonRegular", fontSize: "1.6vmin", height: "3vh", borderRadius: 5, padding: 10 }}
      />
    );
  }
});

const EachHeader = memo(({ columnDetail, lastElement, nodeAnswers, node }) => {
  const languagepreference = useSelector(getBrokerInformation).information.languagepreference;
  const valueColumn = columnDetail?.valueColumn && nodeAnswers?.mainanswer && !columnDetail.hidecalculateValue;
  const tempSum = valueColumn
    ? Object.values(nodeAnswers.tableanswer || {}).reduce((sum, row) => sum + (row?.[columnDetail.code] && node?.logic?.inputType === "currency" ? parseFloat(row[columnDetail.code]) || 0 : node?.logic?.inputType === "number" ? 1 : 0), 0)
    : 0;
  const mainAnswer = typeof nodeAnswers?.mainanswer === "string" ? nodeAnswers.mainanswer.replace(/^\D+/g, "") : nodeAnswers?.mainanswer;
  const headerText = valueColumn
    ? `${columnDetail.columnHeading[languagepreference]} (${((tempSum / mainAnswer) * 100).toFixed(2)} %)`
    : columnDetail.columnHeading?.[languagepreference] || "";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        borderRight: lastElement ? 0 : "1px solid white",
        paddingLeft: "1vmin",
        width: columnDetail?.columnWidth ? `${columnDetail.columnWidth}%` : "10%",
      }}
    >
      <AutoFitTextDiv text={headerText} textsize={16} />
    </div>
  );
});

const TableHeaderSchedule = memo(({ widthDistribution, columnDetails, nodeAnswers, node , showArchived }) => (
  <div
    style={{
      width: "100%",
      backgroundColor: theme.COLORS.secondary_skyblue,
      display: "flex",
      height: "100%",
      borderTopRightRadius: 10,
      borderTopLeftRadius: 10,
      color: "white",
    }}
  >
    <EachHeader columnDetail={{ columnWidth: "1.9" }} />
    {columnDetails.map((columnDetail, index) => (
      <EachHeader key={index} columnDetail={columnDetail} nodeAnswers={nodeAnswers} node={node} lastElement={index + (showArchived ? 3 : 1 ) === columnDetails.length} />
    ))}
    {showArchived && (
      <EachHeader lastElement={true} columnDetail={{ columnWidth: "8.9", columnHeading:{'en' : 'Archived', 'fr': 'Archivé'} }}  />
    )}
  </div>
));

const ImportFileMapper = ({ columnDetails, languagepreference, file, mapHeader, setMapHeader }) => {
 
  const [fileHeaders, setfileHeaders] = useState([]);

  let scheduleHeadings = columnDetails.map(x => x.columnHeading[languagepreference]);

  const rows = [
    { heading: "Item Number", options: ["Item", "Number", "Item ID"], checked: true },
    { heading: "Description", options: ["Type", "Description", "Details"], checked: true },
    { heading: "Serial Number", options: ["Serial", "Serial Number", "SN"], checked: true },
    { heading: "Coverage Type", options: ["Coverage", "Type", "Coverage Type"], checked: true },
    { heading: "Purchase", options: ["Purchase", "Purchase Date", "Acquisition"], checked: false },
  ];

  const handleSelectChange = (event, row) => {
    // setSelectedValue(event.target.value);
    // Do something with the new value
    // console.log(event.target.value);
    // console.log(row);
    setMapHeader((prvMapHeader) => {
      return {
        ...prvMapHeader,
        [row.code]: event.target.value
      }
    })
  };

  useEffect(() => {
    if(!file) return;
   
    const reader = new FileReader();
    let csvHeaders = [];
    reader.onload = (event) => {
      let csv = event.target.result;
      if (file.type === "text/csv") {
        csvHeaders = csv.split(/\r\n|\n|\r/)[0]
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map((v) => v.replace(/^"|"$/g, "").trim());
        
      }
      else if (file.name.includes("xlsx")) {
        const workbook = XLSX.read(csv);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        csv = XLSX.utils.sheet_to_csv(sheet);
        let jsonData = XLSX.utils.sheet_to_json(sheet, {skipHeader: true, header: 1 })
        jsonData = jsonData.filter(row => 
          row.some(cell => cell !== '') // Keep rows with at least one non-empty cell
        );
        csvHeaders = csv.split(/\r\n|\n|\r/)[0]
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map((v) => v.replace(/^"|"$/g, "").trim());
        if(csvHeaders.filter(x => !['', null].includes(x)).length < 3 ){
          // if headers are not on first row, loop thru to find it
          for(let rowx of jsonData){
            if(rowx.filter(x => !['', null].includes(x)).length > 3){
              csvHeaders = rowx.filter(Boolean); // remove any blank spaces
              break;
            }
          }
        }
      } else toast.success("File is not CSV or XLSX type");
      setfileHeaders(csvHeaders)
      let checker = Object.values(mapHeader).every(v => csvHeaders.includes(v) )
      let checkerSome = Object.values(mapHeader).some(v => csvHeaders.includes(v)); // if new csvFile headers match previous csv file upload
      if(!checker){
      }
      if(!checkerSome){
        //delete old mapping as old csv headers are not same at all with new header csv
        setMapHeader({})
      }
    };
    file.type === "text/csv" ? reader.readAsText(file) : reader.readAsArrayBuffer(file);
  }, [file])
  return (
    <div
      style={{
        display: file ? "grid" : "none",
        gridTemplateColumns: "1fr 2fr",
        width: "90%",
        // maxWidth: "600px",
        fontFamily: "Arial, sans-serif",
        margin: "15px auto",
        border: "1px solid #ddd",
      }}
    >
      {/* Header Row */}
      <div
        style={{
          padding: "8px",
          borderBottom: "1px solid #ddd",
          borderRight: "1px solid #ddd",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f2f2f2",
          fontWeight: "bold",
        }}
      >
        {translations[languagepreference].quoteyHeading}
      </div>
      <div
        style={{
          padding: "8px",
          borderBottom: "1px solid #ddd",
          borderRight: "none",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#f2f2f2",
          fontWeight: "bold",
        }}
      >
        {translations[languagepreference].columnHeadingInUploadedDocument}
      </div>
      {/* Data Rows */}
      {columnDetails.map((row, index) => (
        <React.Fragment key={index}>
          <div
            style={{
              padding: "8px",
              borderBottom: index === rows.length - 1 ? "none" : "1px solid #ddd",
              borderRight: "1px solid #ddd",
              display: "flex",
              alignItems: "center",
              backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
            }}
          >
            {row.columnHeading[languagepreference]}
          </div>
          <div
            style={{
              padding: "8px",
              borderBottom: index === rows.length - 1 ? "none" : "1px solid #ddd",
              borderRight: "none",
              display: "flex",
              alignItems: "center",
              backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
            }}
          >
            <select
              defaultValue=""
              value={mapHeader[row.code]}
              style={{
                flex: 1,
                padding: "5px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "white",
                marginRight: "10px",
              }}
              onChange={(e) => handleSelectChange(e, row)}
            >
              <option value="">{translations[languagepreference].selectColumn}...</option>
              {fileHeaders.map((option, idx) => (
                <option key={idx} value={option} >
                  {option}
                </option>
              ))}
            </select>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}



export default Schedule;